"use client"

import React, { useCallback, useMemo } from "react"
import { useRouter } from "next/router"

import {
  AppCoviShellRoute,
  CoviHeaderToggle,
  SOFIA_SIDEBAR_NAV_WIDTH_CLASS,
  SofiaSidebarNav,
} from "@/app-covi"
import { registerAuthRedirectGate } from "@/app-shell/layouts/authRedirectGate"
import { RouteOrganizationGate } from "@/app-shell/layouts/RouteOrganizationGate"
import {
  AppShellContributionsProvider as ShellContributionsProvider,
  type AppShellContributions,
  type DefaultPageHeaderContributionProps,
  type PrimaryNavItemContributionProps,
} from "@/app-shell/navigation/AppShellContributionsContext"
import { DevOrganizationSwitcherModal } from "@/app-shell/navigation/DevOrganizationSwitcherModal"
import {
  ApplicationAdmissionRoute,
  BillingStatusBannerRoute,
  useSignupCompletionRedirect,
} from "@/billing"
import {
  DashboardBreadcrumbTitle,
  storedDashboardNames,
  useDashboardShellState,
  useRenameDashboard,
} from "@/dashboard"
import { AddWidgetButton, DashboardNavItem } from "@/dashboard/navigation"
import {
  FEATURE_FLAGS,
  FeatureFlagsProvider,
  useCovalOnly,
  usePostHydrationFeatureFlagValue,
  useUserManagementFeatureFlag,
} from "@/feature-flags"
import { GettingStartedWidget } from "@/home/navigation"
import { SofiaNotebookActionRoute } from "@/notebooks"
import { IncidentStatusBannerRoute } from "@/settings"
import { LocalWorkerIndicator } from "@/simulate"
import { WorkspaceProviderRoute } from "@/workspaces"
import { Show, useOrganization } from "@clerk/nextjs"

import { useOrgSwitcherModal } from "@/hooks/useOrgSwitcherModal"
import { useQueryParams } from "@/hooks/useQueryParams"
import { useRestrictedAccess } from "@/hooks/useRestrictedAccess"
import { isWidgetConfigRoute } from "@/utils/isDashboardRoute"

import { CommandPalette } from "../components/CommandPalette"
import { GlobalNavigationIndicator } from "../components/GlobalNavigationIndicator"
import { OrganizationPicker } from "../components/OrganizationPicker"
import { HOME_BASE_HREF } from "../config/siteNavigation"
import { DIRECT_SHORTCUTS_BY_BASE_HREF } from "../utils/navigationCommandTree"
import {
  buildAppShellNavigationItems,
  resolveCanCreateHumanReviewProject,
} from "./appShellNavigationItems"
import { RichDefaultPageHeader } from "./RichDefaultPageHeader"
import { ToolCallNameDisplayFromFlag } from "./ToolCallNameDisplayFromFlag"
import { useNavigationFeatureFlagStatus } from "./useNavigationFeatureFlagStatus"
import { WorkspaceSidebarSelector } from "./WorkspaceSidebarSelector"

// Register billing's unfinished-signup redirect as the app-shell auth gate.
// Runs at module load (this route is imported by pages/_app), before
// LogInRequired first renders, so the hook the shell calls stays stable. Inverts
// the dependency: the shell no longer imports the billing module.
registerAuthRedirectGate(useSignupCompletionRedirect)

export const AppShellDomainContributionsProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  return (
    <>
      <Show when="signed-in">
        <RouteOrganizationGate>
          <ApplicationAdmissionRoute renderApplication={renderApplication}>
            {children}
          </ApplicationAdmissionRoute>
        </RouteOrganizationGate>
      </Show>
      <Show when="signed-out">
        <FeatureFlagsProvider>{children}</FeatureFlagsProvider>
      </Show>
    </>
  )
}

function renderApplication(children: React.ReactNode) {
  return (
    <FeatureFlagsProvider>
      <WorkspaceProviderRoute>
        <ToolCallNameDisplayFromFlag>
          <AppShellDomainContributionsInner>
            {children}
          </AppShellDomainContributionsInner>
        </ToolCallNameDisplayFromFlag>
      </WorkspaceProviderRoute>
    </FeatureFlagsProvider>
  )
}

const AppShellDomainContributionsInner = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const queryParams = useQueryParams()
  const { organization, membership } = useOrganization()
  const canCreateHumanReviewProject = resolveCanCreateHumanReviewProject({
    isOrganizationAdmin: membership?.role === "org:admin",
    roleModesEnabled: usePostHydrationFeatureFlagValue(
      FEATURE_FLAGS.HUMAN_REVIEW_ROLE_MODES,
    ),
  })
  const { userEmail } = useRestrictedAccess()
  const featureFlagStatus = useNavigationFeatureFlagStatus()
  const managementEnabled = useUserManagementFeatureFlag()
  const navigationIndicatorEnabled =
    usePostHydrationFeatureFlagValue(
      FEATURE_FLAGS.GLOBAL_NAVIGATION_INDICATOR,
    ) === true

  const getHref = useCallback(
    (baseHref: string) => {
      return queryParams.getHref(baseHref, organization?.slug || undefined)
    },
    [organization?.slug, queryParams],
  )

  // The stable resolver reads the current URL, so its identity cannot key cached hrefs.
  const primaryNavigationItems = buildAppShellNavigationItems({
    featureFlagStatus,
    getHref,
    canCreateHumanReviewProject,
    userEmail,
  })

  const contributions = useMemo<AppShellContributions>(
    () => ({
      primaryNavigationItems,
      renderOrganizationControl:
        managementEnabled === true
          ? ({ onNavigate }) => <OrganizationPicker onNavigate={onNavigate} />
          : undefined,
      renderPrimaryNavItem: (props) => {
        if (props.item.base_href !== "/dashboard") {
          return null
        }

        return <DashboardNavItemContribution {...props} />
      },
      // Sofia's own destinations and chat history hang under the sidebar's
      // Sofia entry on every page, not only inside Home: recent chats are how
      // the user gets back to a conversation from wherever they are, so they
      // stay put rather than appearing and folding away with the section. The
      // sidebar keeps the width those rows need for the same reason.
      renderPrimaryNavItemChildren: ({ item }) =>
        item.base_href === HOME_BASE_HREF ? (
          <SofiaSidebarNav
            investigationsShortcutKey={
              DIRECT_SHORTCUTS_BY_BASE_HREF["/home/investigations"]
            }
            notebooksShortcutKey={
              DIRECT_SHORTCUTS_BY_BASE_HREF["/home/notebooks"]
            }
          />
        ) : null,
      primaryNavWidthClass: SOFIA_SIDEBAR_NAV_WIDTH_CLASS,
      renderSidebarFooterBeforeOrg: ({ orgSlug }) => (
        <>
          {orgSlug && <GettingStartedWidget orgSlug={orgSlug} />}
          <LocalWorkerIndicator />
        </>
      ),
      renderSidebarPrimaryBeforeNav: ({ className }) => (
        <WorkspaceSidebarSelector className={className} />
      ),
      renderDefaultPageHeader: (props) => (
        <DashboardHeaderContribution {...props} />
      ),
      renderHeaderTrailing: ({ className }) => (
        <CoviHeaderToggle className={className} />
      ),
      renderStatusBanner: ({ className }) => (
        <>
          <IncidentStatusBannerRoute className={className} />
          <BillingStatusBannerRoute className={className} />
        </>
      ),
      renderCommandPalette: () => (
        <>
          <CommandPalette featureFlagStatus={featureFlagStatus} />
          <CovalOnlyOrganizationSwitcher />
        </>
      ),
    }),
    [featureFlagStatus, primaryNavigationItems, managementEnabled],
  )

  return (
    <ShellContributionsProvider contributions={contributions}>
      {navigationIndicatorEnabled && <GlobalNavigationIndicator />}
      <AppCoviShellRoute>
        <SofiaNotebookActionRoute />
        {children}
      </AppCoviShellRoute>
    </ShellContributionsProvider>
  )
}

const CovalOnlyOrganizationSwitcher = () => {
  const enabled = useCovalOnly()

  return enabled ? <OrganizationSwitcher /> : null
}

const OrganizationSwitcher = () => {
  const { isOpen, closeModal } = useOrgSwitcherModal()

  return <DevOrganizationSwitcherModal isOpen={isOpen} onClose={closeModal} />
}

const DashboardNavItemContribution = ({
  item,
  getHref,
  isActive,
  isOpen,
  onOpenChange,
}: PrimaryNavItemContributionProps) => {
  const {
    dashboards,
    dashboardsLoading,
    dashboardFetchError,
    retryDashboards,
  } = useDashboardShellState()
  return (
    <DashboardNavItem
      item={item}
      getHref={getHref}
      isActive={isActive}
      dashboards={dashboards}
      isLoading={dashboardsLoading}
      error={dashboardFetchError}
      onRetry={() => void retryDashboards()}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    />
  )
}

const DashboardHeaderContribution = ({
  getHref,
  isDashboardPage,
}: DefaultPageHeaderContributionProps) => {
  const router = useRouter()
  const {
    currentDashboard,
    currentDashboardId,
    dashboardMetadata,
    organizationSlug,
  } = useDashboardShellState()
  const renameDashboard = useRenameDashboard()

  // The widget create/edit page supplies its own header actions, so the
  // dashboard-level "New Widget" button is redundant there.
  const isWidgetConfigPage = isWidgetConfigRoute(router.pathname)

  return (
    <RichDefaultPageHeader
      getHref={getHref}
      isDashboardPage={isDashboardPage}
      dashboardDisplayName={
        isDashboardPage && currentDashboard
          ? currentDashboard.is_default
            ? "Main"
            : currentDashboard.display_name?.trim() || "Untitled"
          : undefined
      }
      dashboardTitle={
        isDashboardPage &&
        !isWidgetConfigPage &&
        currentDashboard &&
        !currentDashboard.is_default ? (
          <DashboardBreadcrumbTitle
            key={currentDashboard.id}
            displayName={currentDashboard.display_name}
            otherNames={storedDashboardNames(
              dashboardMetadata,
              currentDashboard.id,
            )}
            onRename={(name) => renameDashboard(currentDashboard.id, name)}
          />
        ) : undefined
      }
      dashboardHeaderActions={
        isDashboardPage &&
        !isWidgetConfigPage &&
        currentDashboardId &&
        organizationSlug ? (
          <AddWidgetButton
            dashboardId={currentDashboardId}
            organizationSlug={organizationSlug}
          />
        ) : null
      }
    />
  )
}
